import { useState, type MouseEvent } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { FournisseurPerimetre } from '../../site/SiteContext';
import { FournisseurAccessibilite } from '../../accessibilite/FournisseurAccessibilite';
import { PanneauAccessibilite } from '../../accessibilite/PanneauAccessibilite';
import { useReduireAnimations } from '../../lib/useReduireAnimations';
import { FournisseurAppel } from '../../appels/FournisseurAppel';
import { SonnerieEntrante } from '../appels/SonnerieEntrante';
import { AppelSortant } from '../appels/AppelSortant';
import { AppelEnCours } from '../appels/AppelEnCours';
import { PopupReponseRapide } from '../messagerie/PopupReponseRapide';
import { BandeauRappelActions } from './BandeauRappelActions';
import { BandeauApercuMarque } from '../../marque/BandeauApercuMarque';
import { titreDuChemin } from '../../config/ecrans';
import { useTitrePage } from '../../lib/useTitrePage';
import { useAuth } from '../../auth/useAuth';

/** Titre de l'onglet : l'écran de l'adresse affichée (RGAA 8.6). */
function TitreEcran(): null {
  const { pathname } = useLocation();
  useTitrePage(titreDuChemin(pathname));
  return null;
}

/**
 * Lien d'évitement (RGAA 12.7) : premier arrêt de tabulation, il mène au
 * contenu sans repasser par le menu. Le focus est posé à la main : l'ancre
 * seule ajouterait `#contenu` à l'adresse, que le routeur relirait.
 */
function allerAuContenu(evenement: MouseEvent<HTMLAnchorElement>): void {
  evenement.preventDefault();
  document.getElementById('contenu')?.focus();
}

/**
 * Ossature de l'application : sidebar par profil, barre supérieure avec
 * sélecteur de site, zone de contenu. Reprise de la maquette validée.
 *
 * Repères (TF-341) : `nav` pour le menu, `header` pour la barre du haut,
 * `main` pour le seul contenu de l'écran — c'est lui que vise le lien
 * d'évitement, et lui que les modes « Jaune sur noir » et « Focus » habillent.
 */
export function AppShell(): JSX.Element {
  const [navigationOuverte, setNavigationOuverte] = useState(false);
  // Le bouton flottant et ses adaptations : apprenants, formateurs et pôle
  // pédagogique seulement (TF-491).
  const accessibilite = useAuth().peutAcceder('accessibility');
  // Réapplique la préférence de ce poste au chargement, quel que soit l'écran
  // ouvert : elle n'est réglée que depuis « Mes paramètres ».
  useReduireAnimations();

  return (
    <FournisseurPerimetre>
      <FournisseurAccessibilite actif={accessibilite}>
        <FournisseurAppel>
          <TitreEcran />
          <a
            href="#contenu"
            onClick={allerAuContenu}
            className="sr-only rounded-xl bg-codialis-navy text-sm font-semibold text-white shadow-lift focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[130] focus:px-4 focus:py-3"
          >
            Aller au contenu
          </a>
          <div className="flex min-h-screen">
            <Sidebar ouverte={navigationOuverte} onFermer={() => setNavigationOuverte(false)} />

            <div className="flex min-w-0 flex-1 flex-col">
              <Topbar onOuvrirNavigation={() => setNavigationOuverte(true)} />
              {/* Gouttière basse : le bouton flottant d'accessibilité est ancré
                  en bas à gauche et masquait la dernière ligne des listes sur les
                  petites largeurs. Elle disparaît dès que la barre latérale
                  reprend sa place, le bouton n'étant alors plus au-dessus du
                  contenu. Sans bouton, pas de gouttière. */}
              <main
                id="contenu"
                tabIndex={-1}
                className={`min-w-0 flex-1 animate-fade-in overflow-y-auto bg-codialis-cream focus:outline-none ${
                  accessibilite ? 'pb-24 lg:pb-0' : ''
                }`}
              >
                {/* Aperçu de la marque blanche (TF-436), pour la seule personne qui la règle. */}
                <BandeauApercuMarque />
                {/* Rappel de 15 h 30 du formateur (CDC §7.3), au-dessus de tout écran. */}
                <BandeauRappelActions />
                <Outlet />
              </main>
            </div>
          </div>

          {/* Présent sur tous les écrans : agrandir le texte est un besoin
              permanent, pas un réglage à aller chercher dans une rubrique. */}
          {accessibilite && <PanneauAccessibilite />}

          {/* Sonnerie et appel en cours flottent au-dessus de tout écran, peu
              importe la route active — c'est le but du montage ici plutôt que
              dans MessagesPage. */}
          <SonnerieEntrante />
          <AppelSortant />
          <AppelEnCours />

          {/* Réponse rapide façon Discord : posée dès qu'un message arrive,
              peu importe l'écran ouvert (jamais si on est déjà sur /messages,
              ni si le destinataire est en « ne pas déranger », filtré côté API). */}
          <PopupReponseRapide />
        </FournisseurAppel>
      </FournisseurAccessibilite>
    </FournisseurPerimetre>
  );
}
